import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, type Tenant } from '../api';
import { Banner, Card, Copyable, Tabs } from '../components/ui';
import { SamlTab } from './SamlTab';
import { OidcTab } from './OidcTab';
import { PingTab } from './PingTab';
import { ResultsTab } from './ResultsTab';

type TabId = 'saml' | 'oidc' | 'ping' | 'results' | 'settings';
interface SessionTenant { id: string; slug: string; name: string }

/**
 * Self-service tenant console, reached with an admin key rather than an account.
 * Reuses the same protocol tabs the operator console uses.
 */
export function ManagePage() {
  const { slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const [tenant, setTenant] = useState<SessionTenant | null>(null);
  const [state, setState] = useState<'checking' | 'need-key' | 'ready'>('checking');
  const [error, setError] = useState('');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const tab = (params.get('tab') as TabId) ?? 'saml';

  const exchange = async (adminKey: string) => {
    setBusy(true);
    setError('');
    try {
      const { tenant: t } = await api.post<{ tenant: SessionTenant }>(
        `/api/public/tenants/${encodeURIComponent(slug)}/session`, { key: adminKey },
      );
      setTenant(t);
      setState('ready');
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That key was not accepted');
      setState('need-key');
      return false;
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const fromUrl = params.get('k');
    if (fromUrl) {
      // Drop the key from the address bar as soon as it is exchanged: it would otherwise
      // sit in browser history, and leak through the Referer header on outbound links.
      void exchange(fromUrl).then(() => {
        const next = new URLSearchParams(params);
        next.delete('k');
        setParams(next, { replace: true });
      });
      return;
    }
    api.get<{ tenant: SessionTenant | null }>('/api/public/tenant-session')
      .then(({ tenant: t }) => {
        if (t && t.slug === slug) { setTenant(t); setState('ready'); }
        else setState('need-key');
      })
      .catch(() => setState('need-key'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (state === 'checking') return <div className="content"><span className="faint">Loading…</span></div>;

  if (state === 'need-key') {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <h1 className="page-title" style={{ marginBottom: 4 }}>Open “{slug}”</h1>
          <p className="page-sub" style={{ marginBottom: 16 }}>
            Paste the admin key you saved when this tenant was created.
          </p>
          <Card>
            {error && <Banner kind="error">{error}</Banner>}
            <label className="field">
              <span>Admin key</span>
              <input
                type="password" value={key} autoFocus
                onChange={(e) => setKey(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && key) void exchange(key); }}
              />
            </label>
            <button className="primary" disabled={busy || !key} onClick={() => exchange(key)}>
              {busy ? 'Checking…' : 'Open tenant'}
            </button>
          </Card>
          <p className="faint small" style={{ textAlign: 'center' }}>
            Lost the key? It cannot be recovered — only its hash is stored.{' '}
            <Link to="/">Create a new tenant</Link>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="content" style={{ maxWidth: 1100, margin: '0 auto', paddingTop: 26 }}>
      <div className="row" style={{ marginBottom: 18 }}>
        <div>
          <h1 className="page-title">{tenant!.name}</h1>
          <p className="page-sub"><code>{tenant!.slug}</code></p>
        </div>
        <div className="spacer" />
        <a className="badge proto" href={`/t/${tenant!.slug}`} target="_blank" rel="noreferrer">
          Public portal ↗
        </a>
        <button
          className="ghost"
          onClick={async () => {
            await api.post('/api/public/tenant-session/logout');
            setState('need-key');
            setTenant(null);
          }}
        >Sign out</button>
      </div>

      <Tabs<TabId>
        active={tab}
        onChange={(t) => setParams({ tab: t })}
        tabs={[
          { id: 'saml', label: 'SAML SP' },
          { id: 'oidc', label: 'OAuth / OIDC' },
          { id: 'ping', label: 'PingAccess' },
          { id: 'results', label: 'Results' },
          { id: 'settings', label: 'Settings' },
        ]}
      />

      {tab === 'saml' && <SamlTab tenantId={tenant!.id} />}
      {tab === 'oidc' && <OidcTab tenantId={tenant!.id} />}
      {tab === 'ping' && <PingTab tenantId={tenant!.id} />}
      {tab === 'results' && <ResultsTab tenantId={tenant!.id} />}
      {tab === 'settings' && <Settings tenantId={tenant!.id} />}
    </div>
  );
}

function Settings({ tenantId }: { tenantId: string }) {
  const [detail, setDetail] = useState<Tenant | null>(null);

  useEffect(() => { api.get<Tenant>(`/api/tenants/${tenantId}`).then(setDetail).catch(() => {}); }, [tenantId]);
  if (!detail) return <p className="faint">Loading…</p>;

  return (
    <>
      <Card title="Endpoints" hint="Everything for this tenant lives under this prefix.">
        <Copyable label="Public portal" value={detail.portalUrl} />
        <Copyable label="Base URL" value={detail.publicBaseUrl} />
      </Card>

      <Card
        title="Service provider certificate"
        hint="Upload this to any IdP that needs to verify signed AuthnRequests or encrypt assertions to this SP."
      >
        <pre className="code mono" style={{ maxHeight: 200 }}>{detail.spCertificate}</pre>
      </Card>

      <Card title="About this tenant">
        <Banner kind="warning">
          This is a shared public instance. Use test clients and test users — never a
          production client secret. Captured results contain real tokens and are readable by
          anyone holding the result link, so they expire automatically.
        </Banner>
        <p className="hint" style={{ marginBottom: 0 }}>
          Your tenant is not deleted for being idle, so integrations you register at your IdP
          keep working. Created {new Date(detail.createdAt).toLocaleString()}.
        </p>
      </Card>
    </>
  );
}
